import * as Popover from '@radix-ui/react-popover';
import { DOWN_ARROW, END, ESCAPE_KEY, HOME, TAB_KEY, UP_ARROW } from 'constants/keyCodes';
import { useCallback, useMemo, useRef, useState, KeyboardEvent } from 'react';

import { useDropdownPopoverAlignment } from 'ui/utility/hooks/useDropdownPopoverAlignment';
import { HORIZONTAL_ALIGNMENT, VERTICAL_ALIGNMENT } from 'ui/utility/types';
import { BasicOption } from '../BasicOption';
import {
  OptionWrapper,
  SelectContainer,
  SelectWrapper as StyledSelectWrapper,
  SelectOptionsContainer as StyledSelectOptionsContainer,
  TriggerContainer,
} from '../Selects.styles';
import { VirtualizedOptionsList } from '../VirtualizedOptionsList';
import { ALIGNMENT } from '../constants';
import { useAriaSelectProps, useCategorySelection, useFilterText } from '../hooks';
import { SelectProps, SelectValueType, SingleSelectProps } from '../types';
import {
  createDropdownToggle,
  createUniqueOptionsAndDefaults,
  focusHandlerForOptionsList,
} from '../utils';
import { OptionsList } from '../OptionsList';
import { TypeaheadTrigger } from './TypeaheadTrigger';
import { useTypeaheadTrigger } from './useTypeaheadTrigger';

export const TYPEAHEAD_SELECT_DATA_PORTAL_ID = 'typeahead-select-portal';

export const SINGLE_TYPEAHEAD_SELECT_EMPTY_TEXT_TEST_ID =
  'single-typeahead-select-empty-text-test-id';

export const getTypeaheadSelectWrapperTestId = (id: string | number) =>
  `basic-typeahead-select-trigger${id ? `-${id}` : ''}`;

export type TypeaheadBasicSelectProps<TValue extends SelectValueType = string> =
  SelectProps<TValue> & SingleSelectProps;

export function TypeaheadSelect<TValue extends SelectValueType = string>({
  children,
  defaultValues = [], // setting this to a string will break option selection
  id = '',
  isDisabled = false,
  isError = false,
  isRequired = false,
  isReadOnly = false,
  labelId = '',
  onSelect = () => undefined,
  Option = BasicOption,
  options = [],
  placeholder = '',
  Prefix = null,
  Suffix = null,
  title = '',
  SelectWrapper = StyledSelectWrapper,
  SelectOptionsContainer = StyledSelectOptionsContainer,
  horizontalAlignment = ALIGNMENT.LEFT,
  verticalAlignment = ALIGNMENT.DOWN,
  automaticallyAvoidCollisions = false,
  className,
  buttonStyles = {},
  emptyStateText = 'No existing values',
  style = {},
  autoFocus = false,
  isVirtualized = false,
  canFetch = false,
  onFetch = () => undefined,
  isFullWidth = false,
  maxLength,
  disableInternalFiltering = false,
  onSearchChange,
  ...props
}: TypeaheadBasicSelectProps<TValue>) {
  const isExternalSearchUsed = disableInternalFiltering;
  /** Refs - used for focus tracking */
  const parentEl = useRef<HTMLDivElement>(null); // a ref for the wrapper
  const triggerContainerEl = useRef<HTMLDivElement>(null); // the trigger container for the select
  const optionsEl = useRef<HTMLDivElement & HTMLUListElement>(null); // the wrapper of the options

  // used to filter options
  const [filterText, setFilterText] = useFilterText('');

  if (Array.isArray(children)) {
    console.error('Selects cannot accept more than one child node'); // eslint-disable-line
  }

  /**
      Return the options and the defaultOptions as uniques, the memoization
      here is critical to control state thrashing in the useOptionsSelection
      hooks, which may be re-introduced to this component in the future.
      new optionId = parentId + "-" + optionId
      new defaultIds = parentId + "-" + defaultId
    */
  const [uniqueOptions, uniqueDefaults] = useMemo(
    () => createUniqueOptionsAndDefaults<TValue>(id, options, defaultValues),
    [id, options, defaultValues],
  );

  /** see documentation inside useCategorySelection.js */
  const { categoryDetail, getOptions, selectedOptions, singleSelect, setSelectWithoutMatch } =
    useCategorySelection({
      callback: onSelect,
      defaultSelections: uniqueDefaults,
      filterText,
      options: uniqueOptions,
      isExternalSearchUsed,
    });

  /** Build out the associated ARIA props by the state of the component */
  const ariaProps = useAriaSelectProps({
    // if a single node is active, it becomes the activeId on the listbox
    activeId: selectedOptions.length === 1 ? selectedOptions[0].id : '',
    isDisabled,
    isReadOnly,
    isRequired,
    labelId,
    title,
  });

  /**
   * This tracks the open/closed status for the select.
   */
  const [isOpen, setIsOpen] = useState(false);
  const toggleDropdown = createDropdownToggle(setIsOpen, isOpen);

  // this cb executes a fn if any of the listed keys match the keycode
  // documentation on required ARIA keyboard interactions here
  // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/listbox_role
  const handleKeyPress = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const { keyCode } = e;
      try {
        const {
          focusFirstItem,
          focusLastItem,
          focusPreviousItem,
          focusNextItem,
          isFirstItemFocused,
          isLastItemFocused,
          isItemFocused,
        } = focusHandlerForOptionsList(optionsEl);

        const isShiftKeyPressed = e.shiftKey;

        switch (keyCode) {
          case END: {
            focusLastItem();
            break;
          }

          case HOME: {
            focusFirstItem();
            break;
          }

          case ESCAPE_KEY: {
            toggleDropdown(false);
            break;
          }

          case TAB_KEY: {
            if (isItemFocused && isLastItemFocused && !isShiftKeyPressed) {
              e.preventDefault(); // interrupt the tab key
              focusFirstItem();
            }

            if (isItemFocused && isFirstItemFocused && isShiftKeyPressed) {
              e.preventDefault(); // interrupt the tab key
              focusLastItem(); // cycle back to the last element
            }
            break;
          }

          case UP_ARROW: {
            focusPreviousItem();
            break;
          }

          case DOWN_ARROW: {
            focusNextItem();
            break;
          }

          default: {
            return;
          }
        }
      } catch (err) {
        console.error(err); // eslint-disable-line
      }
    },
    [optionsEl],
  );

  const { popoverVerticalAlignment, popoverHorizontalAlignment } = useDropdownPopoverAlignment(
    horizontalAlignment as HORIZONTAL_ALIGNMENT,
    verticalAlignment as VERTICAL_ALIGNMENT,
  );

  const { value, onSearch, onKeyPress, selectCurrentValue } = useTypeaheadTrigger({
    isOpen,
    options,
    optionsEl,
    selectedOptions,
    setFilterText,
    toggleDropdown,
    selectOption: setSelectWithoutMatch,
    onSearchChange,
  });

  const OptionsListComponent = isVirtualized ? VirtualizedOptionsList : OptionsList;
  return (
    <SelectContainer
      data-test={props['data-test'] || `typeahead-select${id ? `-${id}` : ''}`}
      data-select-type="single"
      data-error={isError}
      id={id}
      ref={parentEl}
      className={className}
      style={style}
      isFullWidth={isFullWidth}
    >
      <Popover.Root
        open={isOpen}
        onOpenChange={(newIsOpen) => {
          toggleDropdown(newIsOpen);
          selectCurrentValue(newIsOpen);
        }}
      >
        <TriggerContainer ref={triggerContainerEl}>
          <SelectWrapper
            isNonInteractive={isDisabled}
            id={`trigger-${id}`}
            isError={isError}
            ref={parentEl}
            isFullWidth={isFullWidth}
            {...props}
            data-test={getTypeaheadSelectWrapperTestId(id)}
          >
            <Popover.Trigger asChild>
              <TypeaheadTrigger
                value={value}
                onSearch={onSearch}
                onKeyPress={onKeyPress}
                data-test="typeahead-select-value-display"
                isDisabled={isDisabled}
                placeholder={placeholder}
                autoFocus={autoFocus}
                Prefix={Prefix}
                Suffix={Suffix}
                maxLength={maxLength}
                style={buttonStyles}
                onFocus={(e) => {
                  e.preventDefault();
                  if (!isDisabled && !isReadOnly) {
                    toggleDropdown(true);
                  }
                }}
              />
            </Popover.Trigger>
          </SelectWrapper>
        </TriggerContainer>
        <Popover.Portal>
          <Popover.Content
            asChild
            hideWhenDetached // automatically hide the content when the trigger becomes fully occluded.
            side={popoverVerticalAlignment}
            align={popoverHorizontalAlignment}
            avoidCollisions={automaticallyAvoidCollisions}
            onOpenAutoFocus={(e) => {
              e.preventDefault(); // prevent the popover from stealing focus from the input
            }}
            data-portal-id={TYPEAHEAD_SELECT_DATA_PORTAL_ID}
          >
            <SelectOptionsContainer
              {...props}
              {...ariaProps}
              data-test={`typeahead-select-options-container${id ? `-${id}` : ``}`}
              isFullWidth={isFullWidth}
              verticalAlignment={verticalAlignment}
              horizontalAlignment={horizontalAlignment}
              $triggerWidth={parentEl?.current?.offsetWidth}
            >
              <OptionWrapper
                as="div"
                onKeyDown={handleKeyPress}
                ref={optionsEl}
                role="listbox"
                horizontalAlignment={horizontalAlignment}
                verticalAlignment={verticalAlignment}
                {...ariaProps}
                {...props}
                data-test={`typeahead-select-option-wrapper-${id}`}
                onMouseDown={(e: any) => {
                  e.preventDefault();
                }}
                isVirtualized={isVirtualized}
                isFullWidth={isFullWidth}
              >
                <OptionsListComponent
                  id={id}
                  getOptions={getOptions}
                  filterText={filterText}
                  emptyStateText={emptyStateText}
                  categoryDetail={categoryDetail}
                  singleSelect={singleSelect}
                  Option={Option}
                  toggleDropdown={toggleDropdown}
                  emptyDropdownDataTestId={SINGLE_TYPEAHEAD_SELECT_EMPTY_TEXT_TEST_ID}
                  isOpen={isOpen}
                  canFetch={canFetch}
                  onFetch={onFetch}
                  containerRef={optionsEl}
                />
              </OptionWrapper>
            </SelectOptionsContainer>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </SelectContainer>
  );
}
